import { Ionicons } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { Link, router, useFocusEffect } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Pressable, Text, ScrollView } from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputDropList from "@/components/input/InputDropList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { CounterpartyAddressRequest } from "@/utils/dto/accounts.request";
import { formatSwiftCounterpartiesName } from "@/utils/text/FormatTextUtils";

type FormData = {
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  postalCodeUs: string;
  countryCode: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

interface StateData {
  state: string;
  stateCode: string;
  countryCode: string;
}

interface CityData {
  city: string;
  stateCode: string;
  countryCode: string;
}

export default function AccountAddress() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const [currentTransactionId, setCurrentTransactionId] = useState("");
  const [screenFocused, setScreenFocused] = useState(false);
  const [countryAcronym, setCountryAcronym] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [states, setStates] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [cities, setCities] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [stateAcronym, setStateAcronym] = useState("");
  const [isHovered, setIsHovered] = useState(false);

  const resolver = classValidatorResolver(CounterpartyAddressRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, setValue, formState, reset, setFocus } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  useFocusEffect(
    React.useCallback(() => {
      setScreenFocused(true);
      if (
        currentTransactionId !== globalValues.currentTransactionId ||
        globalValues.currentTransactionId === ""
      ) {
        reset();
        setCurrentTransactionId(globalValues.currentTransactionId);
      }
      return () => {
        setScreenFocused(false);
      };
    }, [globalValues.currentTransactionId, screenFocused])
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountries(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/states", {
          countryCode: countryAcronym,
          searchTerm: stateSearchTerm,
        });
        const mappedStates = response.map((state: StateData) => ({
          value: state.state,
          code: state.stateCode,
          uniqueKey: `state-${state.stateCode}-${state.state}`,
        }));
        setStates(mappedStates);
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };
    if (stateSearchTerm) {
      fetchData();
    }
  }, [stateSearchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/cities", {
          countryCode: countryAcronym,
          stateCode: stateAcronym,
          searchTerm: citySearchTerm,
        });
        const mappedStates = response.map((city: CityData) => ({
          value: city.city,
          code: city.stateCode,
          uniqueKey: `city-${city.city}-${city.stateCode}`,
        }));
        setCities(mappedStates);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    if (citySearchTerm) {
      fetchData();
    }
  }, [citySearchTerm]);

  const removeNullProperties = (obj) => {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === "object") {
        removeNullProperties(obj[key]);
        if (Object.keys(obj[key]).length === 0) {
          delete obj[key];
        }
      } else if (obj[key] === null) {
        delete obj[key];
      } else if (obj[key] === undefined) {
        delete obj[key];
      } else if (obj[key] === "") {
        delete obj[key];
      }
    });
  };

  const onSubmit = async (data: FormData) => {
    if (data.countryCode === "US" && data.state.length > 2) {
      Toast.show({
        type: "error",
        autoHide: false,
        props: {
          text1: t("common:toast.error"),
          text2: t("common:toast.subtitle.statesus"),
        },
      });
      return;
    }
    const jsonData = JSON.stringify(data);
    dispatch(setForm({ accountAddressData: jsonData }));
    const profileInformationData =
      globalValues.profileInformationData !== null
        ? typeof globalValues.profileInformationData === "string"
          ? JSON.parse(globalValues.profileInformationData)
          : globalValues.profileInformationData
        : null;
    const profileAddressData =
      globalValues.profileAddressData !== null
        ? typeof globalValues.profileAddressData === "string"
          ? JSON.parse(globalValues.profileAddressData)
          : globalValues.profileAddressData
        : null;
    const accountInformation = JSON.parse(
      globalValues.accountInformationData || "{}"
    );

    const requestData = {
      counterpartyType: globalValues.counterpartyType,
      rails: [globalValues.rail],
      description: accountInformation?.description || null,
      profile: {
        ...profileInformationData,
        address: {
          ...profileAddressData,
        },
      },
      accountInformation: {
        ...accountInformation,
        institutionAddress: {
          addressLine1: data.addressLine1,
          city: data.city,
          state: data.state,
          countryCode: countryAcronym,
          postalCode: data.postalCodeUs || data.postalCode,
        },
      },
    };

    if (requestData.accountInformation.description) {
      delete requestData.accountInformation.description;
    }
    if (
      requestData.rails[0] === "SWIFT" &&
      requestData.profile.name.length > 35
    ) {
      const { name, addressLine } = formatSwiftCounterpartiesName(
        requestData.profile.name,
        requestData.profile.address.addressLine1
      );
      requestData.profile.name = name;
      requestData.profile.address.addressLine1 = addressLine;
    }
    removeNullProperties(requestData);
    try {
      const request = await sendRequest(
        "post",
        "/counterparty",
        requestData,
        true
      );
      if (request) {
        dispatch(setForm({ counterpartyId: request.id }));
        router.push("home/transaction/counterparty");
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleClick = (data: FormData) => {
    const jsonData = JSON.stringify(data);
    dispatch(setForm({ accountAddressData: jsonData }));
    router.push("home/transaction/intermediary");
  };

  const handleCountry = (data: { value: string; code: string }) => {
    dispatch(setForm({ country: data.value }));
    setCountryAcronym(data.code);
    setValue("countryCode", data.code, { shouldValidate: true });
    setFocus("state");
    setCountries([]);
  };
  const handleState = (data: { value: string; code: string }) => {
    setStateAcronym(data.code);
    if (countryAcronym === "US") {
      setValue("state", data.code, { shouldValidate: true });
    } else {
      setValue("state", data.value, { shouldValidate: true });
    }
    setFocus("city");
    setStates([]);
  };
  const handleCity = (data: { value: string; code: string }) => {
    setValue("city", data.value, { shouldValidate: true });
    setFocus("addressLine1");
    setCities([]);
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1 p-4 lg:p-0">
      <Link href="/home/transaction/account" asChild>
        <View className="absolute top-0 left-0 m-1.5 z-50">
          <Ionicons name="chevron-back-outline" size={28} color="#BDBDBD" />
        </View>
      </Link>
      <Link href="/home" asChild>
        <View className="absolute top-0 right-0 m-1 z-50">
          <Ionicons name="close-outline" size={32} color="#BDBDBD" />
        </View>
      </Link>
      <View className="my-2">
        <Title text={t("home/account:titleaddress")} />
      </View>
      <View className="flex-grow flex-col w-full items-center justify-center">
        <View className="z-50">
          <InputDropList
            label={t("home/account:input.institutioncountry")}
            handleChange={(data) => {
              handleCountry(data);
            }}
            clearValueFunction={() => {
              setCountries([]);
            }}
            control={control}
            name="countryCode"
            data={countries}
            onSearchChange={(term) => setSearchTerm(term)}
            placeholder={t("common:placeholder.country")}
          />
        </View>
        <View className="z-40 flex-col lg:flex-row">
          <View className="flex-col mx-4">
            <View className="z-30">
              <InputDropList
                label={t("home/account:input.institutionstate")}
                handleChange={(data) => {
                  handleState(data);
                }}
                clearValueFunction={() => {
                  setStates([]);
                }}
                control={control}
                name="state"
                data={states}
                onSearchChange={(term) => setStateSearchTerm(term)}
                placeholder={t("common:placeholder.state")}
              />
            </View>
            <View className="z-20">
              <InputDropList
                label={t("home/account:input.institutioncity")}
                handleChange={(data) => {
                  handleCity(data);
                }}
                clearValueFunction={() => {
                  setCities([]);
                }}
                control={control}
                name="city"
                data={cities}
                onSearchChange={(term) => setCitySearchTerm(term)}
                placeholder={t("common:placeholder.city")}
              />
            </View>
          </View>
          <View className="flex-col mx-4">
            <Input
              label={t("home/account:input.institutionaddressline1")}
              control={control}
              name="addressLine1"
              type="alpha"
            />
            <Input
              label={t("home/account:input.institutionpostalcode")}
              control={control}
              name={
                countryAcronym && countryAcronym === "US"
                  ? "postalCodeUs"
                  : "postalCode"
              }
              type="alpha"
            />
          </View>
        </View>
        {globalValues.rail === "SWIFT" ? (
          <Pressable
            className={`items-center justify-center w-60 h-8 rounded-full m-4 z-10 ${
              isHovered ? "bg-blue3" : "bg-blue1"
            }`}
            onPress={handleSubmit(handleClick)}
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
          >
            <Text className="text-white font-normal text-base">
              {t("common:button.addintermediarybank")}
            </Text>
          </Pressable>
        ) : (
          <></>
        )}
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
